import React, { useEffect, useRef, useState } from 'react';
import { getPhoneSetup, getCallTranscriptStatus } from '../../truckmateStore';

// The browser phone: RingCentral's own phone (RingCentral Embeddable) inside the console. Each dispatcher signs in with their
// RingCentral login once; calls go out from the company numbers, and every 📞 Call button dials through it when it's signed in.
// RingCentral records the calls; the backend turns the recordings into transcripts on the load (calltranscripts.js).
const ADAPTER = 'https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/adapter.js';
const ON_KEY = 'ta-rc-phone';
export const phone = { loaded: false, loggedIn: false, subs: new Set() };
const tell = () => phone.subs.forEach((f) => f());
const store = (v) => { try { localStorage.setItem(ON_KEY, v ? '1' : ''); } catch { /* private window */ } };
const wanted = () => { try { return localStorage.getItem(ON_KEY) === '1'; } catch { return false; } };

// the phone's messages: signed in or out
function listen() {
  if (listen.done) return; listen.done = true;
  window.addEventListener('message', (e) => {
    const d = e && e.data;
    if (!d || typeof d !== 'object') return;
    if (d.type === 'rc-login-status-notify') { phone.loggedIn = !!d.loggedIn; tell(); }
  });
}
export function loadPhone({ clientId, server }) {
  if (phone.loaded || !clientId) return;
  listen();
  const s = document.createElement('script');
  s.src = `${ADAPTER}?${new URLSearchParams({ clientId, appServer: server || 'https://platform.ringcentral.com' })}`;
  s.async = true;
  document.body.appendChild(s);
  phone.loaded = true; tell();
}
const frame = () => document.querySelector('#rc-widget-adapter-frame');
// dial a number on the browser phone (it opens and starts the call)
export function dialOnPhone(number) {
  const f = frame();
  if (!f || !f.contentWindow) return false;
  f.contentWindow.postMessage({ type: 'rc-adapter-new-call', phoneNumber: number, toCall: true }, '*');
  try { if (window.RCAdapter && window.RCAdapter.setMinimized) window.RCAdapter.setMinimized(false); } catch { /* older phone */ }
  return true;
}
export function usePhone() {
  const [, tick] = useState(0);
  useEffect(() => { const f = () => tick((n) => n + 1); phone.subs.add(f); return () => { phone.subs.delete(f); }; }, []);
  return phone;
}

// The header button: turns the phone on, shows whether it's signed in, and how recordings and transcripts are doing
export default function PhoneButton() {
  const p = usePhone();
  const [open, setOpen] = useState(false);
  const [setup, setSetup] = useState(null);
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState('');
  const box = useRef(null);
  useEffect(() => {
    // a dispatcher who had it on gets it back on the next visit
    if (!wanted()) return;
    Promise.resolve().then(() => getPhoneSetup()).then((r) => { setSetup(r); if (r && r.clientId) loadPhone(r); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    Promise.resolve().then(() => getCallTranscriptStatus()).then(setStatus).catch(() => {});
    const outside = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  const turnOn = async () => {
    setErr('');
    try { const r = setup || await getPhoneSetup(); setSetup(r); if (!r.clientId) return; loadPhone(r); store(true); } catch (ex) { setErr(ex.message || 'Could not start the phone.'); }
  };
  const turnOff = () => { store(false); window.location.reload(); };
  const dot = p.loggedIn ? '#16a34a' : p.loaded ? '#d97706' : '#9ca3af';
  return (
    <span ref={box} style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" className="tmw-btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 4, background: dot, display: 'inline-block' }} />
        Phone{p.loggedIn ? '' : p.loaded ? ' (sign in)' : ''}
      </button>
      {open && (
        <div role="dialog" aria-label="Browser phone" className="tmw-acc" style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 60, width: 340, padding: 14, background: '#fff', boxShadow: '0 8px 24px rgba(0,0,0,.12)' }}>
          <b>Browser phone (RingCentral)</b>
          <div className="tmw-small" style={{ marginTop: 6 }}>
            {p.loggedIn ? 'Signed in. Every 📞 Call button dials from here, from the company number. Use a headset.'
              : p.loaded ? 'Sign in with your own RingCentral login in the phone at the bottom right of the page.'
                : 'Call from the console with a headset: sign in once with your RingCentral login.'}
          </div>
          {setup && !setup.clientId && <div className="tmw-note" style={{ marginTop: 8 }}>Not set up yet: an admin adds the RingCentral phone app (Embeddable) client ID in Render as RC_EMBEDDABLE_CLIENT_ID. Until then 📞 Call rings your own phone first.</div>}
          {err && <div className="tmw-small" role="alert" style={{ color: '#b91c1c', marginTop: 6 }}>{err}</div>}
          <div style={{ marginTop: 10 }}>
            {!p.loaded ? <button type="button" className="tmw-btn tmw-btn-sm tmw-btn-primary" onClick={turnOn}>Turn on the phone</button>
              : <button type="button" className="tmw-btn tmw-btn-sm" onClick={turnOff}>Turn off the phone</button>}
          </div>
          <div className="tmw-small" style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
            <b>Recordings and transcripts</b><br />
            {!status ? 'Checking…'
              : status.on ? <>On: recorded calls show in Calls &amp; texts (transcript + summary) and on the load.{status.lastRun ? ` Last check ${new Date(status.lastRun).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}.` : ''} {status.minutesToday} of {status.dailyLimit} min today.</>
                : status.off ? 'Turned off (CALL_TRANSCRIPTS=off).'
                  : `Off: needs ${(status.needs || []).join(', ')} in Render.`}
            {status && status.lastError && <div style={{ color: '#b45309', marginTop: 4 }}>{status.lastError}</div>}
          </div>
        </div>
      )}
    </span>
  );
}
